import useAccessControls from 'modules/access-controls/atoms/use-access-controls';
import useCategoriesSelect from 'modules/categories/hooks/use-categories-select';
import {PriceModeEnum, ProductProps} from 'modules/products/types';
import productsKeys from 'modules/products/util/products-keys';
import React from 'react';
import {BiSortAlt2} from 'react-icons/bi';
import {IoDuplicate, IoTrash} from 'react-icons/io5';
import {useNavigate} from 'react-router-dom';
import StatusButton from 'shared/components/status-button';
import List from 'shared/layout/list';
import ApiDynamicButton from 'shared/migration-components/api-dynamic-button';
import ApiSwitch from 'shared/migration-components/api-switch';
import useProjection from 'shared/migration-hooks/use-projection';
import toBrl from 'shared/util/to-brl';

const Products: React.FC = () => {
  const projection = useProjection<ProductProps>(
    'products',
    [
      'name',
      'categoryName',
      'companyName',
      'priceMode',
      'price',
      'sizes',
      'active',
      'id',
    ],
    {orderBy: {field: 'name', order: 1}},
  );
  const {cityId, companyId} = useAccessControls();
  const navigate = useNavigate();
  const categoriesSelect = useCategoriesSelect();

  return (
    <List<ProductProps>
      {...{
        titleBar: {
          title: 'Produtos',
          actionButton: {title: 'NOVO PRODUTO', to: 'create'},
        },
        table: {
          url: '/products',
          toolbar: [
            {
              label: 'Ordenação no app',
              icon: <BiSortAlt2 />,
              onClick: () => navigate('reorder'),
            },
          ],
          projection: {
            ...projection,
            options: [
              'name',
              'description',
              {
                value: 'categoryName',
                column: (row) => row.categoryName,
                filter: {
                  values: categoriesSelect.data,
                  type: 'select',
                  search: true,
                },
                filterKey: 'categoryId',
              },
              {
                value: 'companyName',
                column: (row) => row.companyName,
                hidden: !!companyId,
              },
              {
                value: 'cityName',
                column: (row) => row.cityName,
                hidden: !!cityId || !!companyId,
              },
              {
                value: 'priceMode',
                column: (row) => (
                  <StatusButton
                    text={
                      row.priceMode === PriceModeEnum.fixed
                        ? 'Fixo'
                        : row.priceMode === PriceModeEnum.additionals
                        ? 'Adicionais'
                        : 'Tamanho'
                    }
                    variant={
                      row.priceMode === PriceModeEnum.fixed
                        ? 'warn'
                        : row.priceMode === PriceModeEnum.additionals
                        ? 'success'
                        : 'info'
                    }
                  />
                ),
                filter: {
                  type: 'select',
                  values: [
                    {label: 'Fixo', value: PriceModeEnum.fixed},
                    {label: 'Tamanho', value: PriceModeEnum.size},
                    {label: 'Adicionais', value: PriceModeEnum.additionals},
                  ],
                },
              },
              {
                value: 'price',
                column: (row) => (row.price ? toBrl(row.price) : ''),
                filter: {valueAsNumber: true},
              },
              {
                value: 'minPrice',
                column: (row) => (row.minPrice ? toBrl(row.minPrice) : ''),
                filter: {valueAsNumber: true},
              },
              {
                value: 'sizes',
                column: (row) => row.sizes?.map((size) => size.name).join(', '),
              },
              {
                value: 'active',
                column: {
                  value: (row) => (
                    <ApiSwitch
                      multipart
                      isChecked={row.active}
                      url={`/products/${row.id}`}
                      queryKey={productsKeys.list()}
                    />
                  ),
                  stopPropagation: true,
                },
                filter: {
                  type: 'select',
                  values: [
                    {
                      label: 'Sim',
                      value: true,
                    },
                    {
                      label: 'Não',
                      value: false,
                    },
                  ],
                },
              },
              {
                value: 'id',
                label: 'Ações',
                column: (row) => (
                  <div style={{display: 'flex', gap: 5}}>
                    <ApiDynamicButton
                      request={{
                        url: `/products/${row.id}/duplicate`,
                        errorTitle: 'Erro ao duplicar produto',
                        method: 'POST',
                      }}
                      button={{
                        icon: <IoDuplicate />,
                        tooltip: 'Duplicar produto',
                      }}
                      keys={{createInfiniteList: productsKeys.list()}}
                    />
                    <ApiDynamicButton
                      keys={{deleteInfiniteList: productsKeys.list()}}
                      button={{icon: <IoTrash />, tooltip: 'Remover produto'}}
                      request={{
                        errorTitle: 'Erro ao remover produto',
                        method: 'DELETE',
                        url: `/products/${row.id}`,
                      }}
                      confirmButtonText="produto"
                    />
                  </div>
                ),
              },
            ],
          },
          translatedPropsKey: 'ProductProps',
        },
        queryKey: productsKeys.list(),
      }}
    />
  );
};

export default Products;
